import * as styles from "./OurCentresMap.module.scss";

import { Circle, GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import CentreMapOverlayCard from "../../../components/SignPostCards/CentreMapOverlayCard";
import CurvedBorder from "../../../components/borders/CurvedBorder";
import { useOurCentres } from "../../../context";
import { withSkipMap } from "../../../hoc/withSkipMap";
import { Colours } from "../../../utils/colours";
import { GOOGLE_API_LIBRARIES } from "../../../utils/google";
import CentreMarker from "../CentreMarker/CentreMarker";

const containerStyle: React.CSSProperties = {
	position: "relative",
	width: "100%",
	height: "100%",
};

const center = {
	lat: -2.605056,
	lng: 51.4555904,
};

const OurCentresMap: React.FC = () => {
	const {
		userLocation,
		currentDistance,
		centreLocations,
		centresInRange,
		onSelectCentre,
		selectedCentreCode,
	} = useOurCentres();

	const [map, setMap] = useState<google.maps.Map>(null);
	const [userLatLng, setUserLatLng] = useState<google.maps.LatLng | null>(null);

	const onLoad = useCallback(
		function callback(callbackMap: google.maps.Map) {
			window.google.maps.event.addListenerOnce(callbackMap, "idle", () => {
				document.querySelector<HTMLElement>(
					"#centresMapContainerId iframe",
				).title = "Centres map";
			});

			const bounds = new window.google.maps.LatLngBounds(
				new window.google.maps.LatLng({
					lat: Number.parseFloat(centreLocations[0].field_latitude),
					lng: Number.parseFloat(centreLocations[0].field_longitude),
				}),
			);
			for (const centre of centreLocations) {
				bounds.extend(
					new window.google.maps.LatLng({
						lat: Number.parseFloat(centre.field_latitude),
						lng: Number.parseFloat(centre.field_longitude),
					}),
				);
			}
			callbackMap.fitBounds(bounds);
			setMap(callbackMap);
		},
		[centreLocations],
	);

	const onUnmount = useCallback(function callback() {
		setMap(null);
	}, []);

	const centresInRangeToShow = useMemo(
		() =>
			centresInRange?.filter((centre) => !centre.field_is_virtual_centre) ||
			([] as Queries.RehomingCentreDataFragment[]),
		[centresInRange],
	);
	const getFitBounds = useCallback(() => {
		if (centresInRangeToShow?.length > 0 && map) {
			const bounds = new window.google.maps.LatLngBounds(
				new window.google.maps.LatLng({
					lat: Number.parseFloat(centresInRangeToShow[0].field_latitude),
					lng: Number.parseFloat(centresInRangeToShow[0].field_longitude),
				}),
			);
			for (const centre of centresInRangeToShow) {
				bounds.extend(
					new window.google.maps.LatLng({
						lat: Number.parseFloat(centre.field_latitude),
						lng: Number.parseFloat(centre.field_longitude),
					}),
				);
			}
			if (userLocation?.latitude) {
				bounds.extend(
					new window.google.maps.LatLng({
						lat: userLocation.latitude,
						lng: userLocation.longitude,
					}),
				);
				setUserLatLng(
					new window.google.maps.LatLng({
						lat: userLocation.latitude,
						lng: userLocation.longitude,
					}),
				);
			}
			map.fitBounds(bounds);
		}
	}, [centresInRangeToShow, map, userLocation]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: changes to centreLocations should trigger a re-render
	useEffect(() => {
		getFitBounds();
		window.addEventListener("resize", getFitBounds);

		return () => window.removeEventListener("resize", getFitBounds);
	}, [centreLocations, getFitBounds]);

	const selectedCentre = useMemo(
		() =>
			centreLocations.find(
				(centre) =>
					centre.relationships.field_centre_code.name === selectedCentreCode,
			),
		[centreLocations, selectedCentreCode],
	);
	const { isLoaded: googleIsLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: process.env.GATSBY_GOOGLE_API_KEY,
		libraries: GOOGLE_API_LIBRARIES,
	});

	return (
		<div className={styles.mapcontainer} aria-hidden>
			<div className={styles.bordersection}>
				<CurvedBorder
					className={styles.bordertop}
					fill={Colours.SANDY_FUR}
					rotation={0}
				/>
			</div>

			<div className={styles.mapwrapper}>
				{googleIsLoaded && (
					<GoogleMap
						id="centresMapContainerId"
						options={{
							fullscreenControl: false,
							minZoom: 6,
							maxZoom: 10,
							mapId: "b2a09d2327650d38",
							mapTypeControl: false,
							streetViewControl: false,
						}}
						mapContainerStyle={containerStyle}
						center={
							userLocation !== null
								? {
										lat: userLocation.latitude,
										lng: userLocation.longitude,
									}
								: center
						}
						onLoad={onLoad}
						onUnmount={onUnmount}
					>
						{centresInRangeToShow?.map((centre) => (
							<CentreMarker
								key={centre.relationships.field_centre_code.name}
								isSelected={
									centre.relationships.field_centre_code.name ===
									selectedCentreCode
								}
								onClick={() =>
									onSelectCentre(centre.relationships.field_centre_code.name)
								}
								latitude={Number.parseFloat(centre.field_latitude)}
								longitude={Number.parseFloat(centre.field_longitude)}
								isVirtual={centre.field_is_virtual_centre}
							/>
						))}
						{userLatLng && userLocation?.latitude && (
							<>
								<Circle
									center={userLatLng}
									radius={currentDistance * 1600}
									options={{
										fillColor: Colours.BLUE_SKY_DARK,
										fillOpacity: 0.2,
										strokeColor: Colours.BLUE_SKY_DARK,
										strokeOpacity: 0.5,
										strokeWeight: 1,
										map: map,
										visible: true,
									}}
								/>
								<Circle
									center={userLatLng}
									radius={10}
									options={{
										fillColor: Colours.BLUE_SKY_DARK,
										fillOpacity: 1,
										strokeColor: Colours.BLUE_SKY_DARK,
										strokeOpacity: 1,
										strokeWeight: 10,
										map: map,
										visible: true,
									}}
								/>
							</>
						)}
					</GoogleMap>
				)}
				{selectedCentreCode && (
					<div className={styles.cardoverlaywrapper}>
						<CentreMapOverlayCard
							key={selectedCentre.title}
							imgUrl={
								selectedCentre.relationships?.field_centre_images?.[0]
									.field_media_image.uri || ""
							}
							url={selectedCentre?.path?.alias}
							centreName={selectedCentre.title}
							city={selectedCentre.field_city}
							postCode={selectedCentre.field_postcode}
							phoneNumber={selectedCentre.field_phone_number}
							googleMapsUrl={selectedCentre.field_google_maps_link}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default withSkipMap(OurCentresMap);
